import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import SectionHeading from "@/components/shared/SectionHeading";
import CtaSection from "@/components/shared/CtaSection";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { STATS } from "@/data/site";

const SECTION = "max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28";

const VALUES = [
  { title: "Evidence-based", body: "We optimize for measurable AI visibility outcomes, not vanity metrics or hype." },
  { title: "Consultative", body: "We act as a strategic partner to leadership, translating AI search into business value." },
  { title: "Transparent", body: "Clear reporting on citations, share of voice and progress against your roadmap." },
  { title: "Enterprise-grade", body: "A structured, auditable methodology built for accountability and scale." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About CompuBrain AI — GEO Consulting Experts"
        description="CompuBrain AI is the GEO division of CompuBrain: 20+ years of digital expertise, 5300+ clients and 16+ industries, now dedicated to Generative Engine Optimization and AI search visibility."
        path="/about"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <PageHero
        eyebrow="About Us"
        titleLines={["Two decades of", "digital expertise,", "focused on AI search."]}
        sub="CompuBrain AI is the Generative Engine Optimization division of CompuBrain — bringing enterprise digital experience into the age of AI-powered search."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "About" }]}
      />

      <section className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Who We Are" title="A consulting partner for the AI era" className="mb-8" />
            <div className="space-y-5 text-lg text-gray-600 leading-relaxed">
              <Reveal><p>CompuBrain has spent over 20 years helping 5300+ businesses across 16+ industries grow their digital presence. CompuBrain AI extends that expertise into Generative Engine Optimization — the discipline of making brands visible, trusted and recommended by AI search engines and large language models.</p></Reveal>
              <Reveal delay={0.05}><p>We are a consulting and implementation partner, not a chatbot development company and not a generic marketing agency. Businesses hire us to be the answer when their customers ask ChatGPT, Gemini, Claude or Perplexity.</p></Reveal>
              <Reveal delay={0.1}><p>Headquartered in Ahmedabad, Gujarat, India, we serve enterprises, SMEs and startups worldwide with a dedicated team of GEO strategists and engineers.</p></Reveal>
            </div>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-8 grid grid-cols-2 gap-8">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-4xl font-extrabold tracking-tightest text-[#0B63F6]">{s.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] border-y border-gray-100">
        <div className={SECTION}>
          <SectionHeading eyebrow="Our Values" title="How we work" className="mb-12" />
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => (
              <StaggerItem key={v.title}>
                <div className="h-full rounded-2xl border border-gray-200 bg-white p-7">
                  <h3 className="font-display text-lg font-bold tracking-tight">{v.title}</h3>
                  <p className="text-[15px] text-gray-600 mt-3 leading-relaxed">{v.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className={SECTION}>
        <div className="rounded-3xl border border-gray-200 p-10 lg:p-14 bg-white">
          <SectionHeading eyebrow="E-E-A-T" title="Experience, Expertise, Authoritativeness, Trust" sub="Everything we recommend to clients, we practise on our own digital presence — from structured data and entity authority to answer-first content engineered for citation." />
        </div>
      </section>

      <CtaSection heading="Let's build your AI visibility together" sub="Partner with a team that has guided 5300+ businesses through digital change." />
    </>
  );
}
